import React, { useState, useEffect, useCallback } from 'react';
import { getUnprocessedPayments, recoverOrderFromPayment, markPaymentAsFailed } from '../../services/api';
import { formatCurrency } from '../../utils/currency';
import { useToast } from '../../contexts/ToastContext';
import PasswordConfirmModal from '../../components/PasswordConfirmModal';

function UnprocessedPayments() {
  const { showSuccess, showError } = useToast();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(null);
  const [markingFailed, setMarkingFailed] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showMarkFailedModal, setShowMarkFailedModal] = useState(false);
  const [failedReason, setFailedReason] = useState('');
  const [selectedPayments, setSelectedPayments] = useState(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        return;
      }
      const data = await getUnprocessedPayments(token);
      setPayments(data.payments || []);
    } catch (error) {
      console.error('Failed to fetch unprocessed payments:', error);
      showError(error.message || 'Failed to load unprocessed payments.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleRecoverOrder = (payment) => {
    if (!payment.has_order_data) {
      showError('Cannot recover order: Order data is missing from this payment.');
      return;
    }

    setPendingAction({ type: 'recover', payment });
    setShowPasswordModal(true);
  };

  const confirmRecoverOrder = async () => {
    if (!pendingAction || pendingAction.type !== 'recover') return;

    const payment = pendingAction.payment;
    try {
      setRecovering(payment.id);
      const token = localStorage.getItem('auth_token');
      const result = await recoverOrderFromPayment(payment.client_reference, token);
      
      showSuccess(`Order recovered successfully! Order Number: ${result.order?.order_number || 'N/A'}`);
      
      // Remove from selection if selected
      const newSelected = new Set(selectedPayments);
      newSelected.delete(payment.id);
      setSelectedPayments(newSelected);
      
      // Refresh the list
      await fetchPayments();
    } catch (error) {
      console.error('Failed to recover order:', error);
      showError(error.message || 'Failed to recover order. Please try again.');
    } finally {
      setRecovering(null);
      setPendingAction(null);
    }
  };

  const handleMarkAsFailed = (payment) => {
    setPendingAction({ type: 'markFailed', payment });
    setShowMarkFailedModal(true);
  };

  const confirmMarkAsFailed = async () => {
    if (!pendingAction || pendingAction.type !== 'markFailed') return;

    const payment = pendingAction.payment;
    try {
      setMarkingFailed(payment.id);
      const token = localStorage.getItem('auth_token');
      await markPaymentAsFailed(payment.client_reference, failedReason || 'Payment not found in Hubtel dashboard', token);
      
      showSuccess('Payment marked as failed successfully.');
      setFailedReason('');
      
      // Refresh the list
      await fetchPayments();
    } catch (error) {
      console.error('Failed to mark payment as failed:', error);
      showError(error.message || 'Failed to mark payment as failed. Please try again.');
    } finally {
      setMarkingFailed(null);
      setPendingAction(null);
      setShowMarkFailedModal(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedPayments(new Set(payments.map(p => p.id)));
    } else {
      setSelectedPayments(new Set());
    }
  };

  const handleSelectPayment = (paymentId) => {
    const newSelected = new Set(selectedPayments);
    if (newSelected.has(paymentId)) {
      newSelected.delete(paymentId);
    } else {
      newSelected.add(paymentId);
    }
    setSelectedPayments(newSelected);
  };

  const isAllSelected = payments.length > 0 && selectedPayments.size === payments.length;
  const isIndeterminate = selectedPayments.size > 0 && selectedPayments.size < payments.length;

  // Get selected payments with order data (for bulk recover)
  const getSelectedRecoverablePayments = () => {
    return payments.filter(p => selectedPayments.has(p.id) && p.has_order_data);
  };

  // Get selected payments that aren't already failed (for bulk mark failed)
  const getSelectedMarkablePayments = () => {
    return payments.filter(p => selectedPayments.has(p.id) && p.status !== 'failed');
  };

  // Bulk recover orders
  const handleBulkRecover = () => {
    const recoverablePayments = getSelectedRecoverablePayments();
    if (recoverablePayments.length === 0) {
      showError('No payments with order data selected. Please select payments that have order data available.');
      return;
    }
    setPendingAction({ type: 'bulkRecover', payments: recoverablePayments });
    setShowPasswordModal(true);
  };

  const confirmBulkRecover = async () => {
    if (!pendingAction || pendingAction.type !== 'bulkRecover') return;

    const paymentsToRecover = pendingAction.payments;
    try {
      setBulkProcessing(true);
      const token = localStorage.getItem('auth_token');
      const results = [];
      const errors = [];

      for (const payment of paymentsToRecover) {
        try {
          const result = await recoverOrderFromPayment(payment.client_reference, token);
          results.push({ payment, result });
        } catch (error) {
          errors.push({ payment, error: error.message });
        }
      }

      if (results.length > 0) {
        showSuccess(`Successfully recovered ${results.length} order(s).`);
      }
      if (errors.length > 0) {
        showError(`Failed to recover ${errors.length} order(s). Check console for details.`);
        console.error('Bulk recover errors:', errors);
      }

      setSelectedPayments(new Set());
      await fetchPayments();
    } catch (error) {
      console.error('Bulk recover failed:', error);
      showError(error.message || 'Failed to recover orders. Please try again.');
    } finally {
      setBulkProcessing(false);
      setPendingAction(null);
    }
  };

  // Bulk mark as failed
  const handleBulkMarkFailed = () => {
    const markablePayments = getSelectedMarkablePayments();
    if (markablePayments.length === 0) {
      showError('No payments selected or all selected payments are already marked as failed.');
      return;
    }
    setPendingAction({ type: 'bulkMarkFailed', payments: markablePayments });
    setShowMarkFailedModal(true);
  };

  const confirmBulkMarkFailed = async () => {
    if (!pendingAction || pendingAction.type !== 'bulkMarkFailed') return;

    const paymentsToMark = pendingAction.payments;
    try {
      setBulkProcessing(true);
      const token = localStorage.getItem('auth_token');
      const results = [];
      const errors = [];

      for (const payment of paymentsToMark) {
        try {
          await markPaymentAsFailed(payment.client_reference, failedReason || 'Payment not found in Hubtel dashboard', token);
          results.push(payment);
        } catch (error) {
          errors.push({ payment, error: error.message });
        }
      }

      if (results.length > 0) {
        showSuccess(`Successfully marked ${results.length} payment(s) as failed.`);
      }
      if (errors.length > 0) {
        showError(`Failed to mark ${errors.length} payment(s) as failed. Check console for details.`);
        console.error('Bulk mark failed errors:', errors);
      }

      setSelectedPayments(new Set());
      setFailedReason('');
      await fetchPayments();
    } catch (error) {
      console.error('Bulk mark failed:', error);
      showError(error.message || 'Failed to mark payments as failed. Please try again.');
    } finally {
      setBulkProcessing(false);
      setPendingAction(null);
      setShowMarkFailedModal(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading unprocessed payments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-md p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Unprocessed Payments</h1>
            <p className="text-sm md:text-base text-gray-600 mt-1">
              Payments that were successful but orders weren't created
            </p>
          </div>
          <div className="flex items-center gap-3">
            {selectedPayments.size > 0 && (
              <div className="bg-blue-100 text-blue-800 px-3 py-1.5 rounded-lg font-semibold text-sm md:text-base">
                {selectedPayments.size} Selected
              </div>
            )}
            <div className="bg-orange-100 text-orange-800 px-3 py-1.5 rounded-lg font-semibold text-sm md:text-base">
              {payments.length} {payments.length === 1 ? 'Payment' : 'Payments'}
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {selectedPayments.size > 0 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between flex-wrap gap-3">
            <div className="text-sm font-medium text-gray-700">
              {selectedPayments.size} payment(s) selected
            </div>
            <div className="flex gap-2 flex-wrap">
              {getSelectedRecoverablePayments().length > 0 && (
                <button
                  onClick={handleBulkRecover}
                  disabled={bulkProcessing}
                  className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkProcessing && pendingAction?.type === 'bulkRecover' ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Recovering...
                    </>
                  ) : (
                    `Recover Selected (${getSelectedRecoverablePayments().length})`
                  )}
                </button>
              )}
              {getSelectedMarkablePayments().length > 0 && (
                <button
                  onClick={handleBulkMarkFailed}
                  disabled={bulkProcessing}
                  className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {bulkProcessing && pendingAction?.type === 'bulkMarkFailed' ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Marking...
                    </>
                  ) : (
                    `Mark Failed (${getSelectedMarkablePayments().length})`
                  )}
                </button>
              )}
              <button
                onClick={() => setSelectedPayments(new Set())}
                disabled={bulkProcessing}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {payments.length === 0 ? (
          <div className="text-center py-12">
            <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-gray-600 text-lg font-medium">No unprocessed payments</p>
            <p className="text-gray-500 text-sm mt-1">All payments have been processed successfully.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider w-12">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Payment Reference
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Order Data
                  </th>
                  <th className="px-3 md:px-6 py-3 text-left text-xs md:text-sm font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {payments.map((payment) => (
                  <tr key={payment.id} className={`hover:bg-gray-50 ${selectedPayments.has(payment.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-3 md:px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedPayments.has(payment.id)}
                        onChange={() => handleSelectPayment(payment.id)}
                        disabled={bulkProcessing || recovering === payment.id || markingFailed === payment.id}
                        className="rounded border-gray-300 text-primary focus:ring-primary disabled:opacity-50"
                      />
                    </td>
                    <td className="px-3 md:px-6 py-4">
                      <div className="text-xs md:text-sm font-mono text-gray-900 truncate max-w-[120px] md:max-w-[150px]" title={payment.client_reference}>
                        {payment.client_reference}
                      </div>
                      {payment.checkout_id && (
                        <div className="text-xs text-gray-500 mt-1 truncate max-w-[120px] md:max-w-[150px]" title={payment.checkout_id}>
                          {payment.checkout_id.substring(0, 15)}...
                        </div>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      {payment.user ? (
                        <div>
                          <div className="text-xs md:text-sm font-medium text-gray-900">
                            {payment.user.name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">{payment.user.email}</div>
                          {payment.user.phone && (
                            <div className="text-xs text-gray-500">{payment.user.phone}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs md:text-sm text-gray-500">User ID: {payment.user_id}</span>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <div className="text-xs md:text-sm font-semibold text-gray-900">
                        {formatCurrency(payment.amount)}
                      </div>
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        payment.status === 'successful'
                          ? 'bg-green-100 text-green-800'
                          : payment.status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {payment.status}
                      </span>
                      {payment.response_code && (
                        <div className="text-xs text-gray-500 mt-1">
                          Code: {payment.response_code}
                        </div>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-xs md:text-sm text-gray-500">
                      {formatDate(payment.created_at)}
                    </td>
                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                      {payment.has_order_data ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          ✓ Available
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          ✗ Missing
                        </span>
                      )}
                    </td>
                    <td className="px-3 md:px-6 py-4 text-xs md:text-sm font-medium">
                      <div className="flex flex-row gap-2 items-center">
                        {payment.has_order_data ? (
                          <button
                            onClick={() => handleRecoverOrder(payment)}
                            disabled={recovering === payment.id || markingFailed === payment.id}
                            className={`inline-flex items-center justify-center px-2 md:px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                              recovering === payment.id
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-primary text-white hover:bg-primary-dark'
                            }`}
                          >
                            {recovering === payment.id ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="hidden sm:inline">Recovering...</span>
                                <span className="sm:hidden">...</span>
                              </>
                            ) : (
                              <>
                                <span className="hidden sm:inline">Recover</span>
                                <span className="sm:hidden">✓</span>
                              </>
                            )}
                          </button>
                        ) : (
                          <span className="text-gray-400 text-xs whitespace-nowrap">N/A</span>
                        )}
                        <button
                          onClick={() => handleMarkAsFailed(payment)}
                          disabled={recovering === payment.id || markingFailed === payment.id || payment.status === 'failed'}
                          className={`inline-flex items-center justify-center px-2 md:px-3 py-1.5 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                            markingFailed === payment.id || payment.status === 'failed'
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : 'bg-red-600 text-white hover:bg-red-700'
                          }`}
                        >
                          {markingFailed === payment.id ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="hidden sm:inline">Marking...</span>
                              <span className="sm:hidden">...</span>
                            </>
                          ) : payment.status === 'failed' ? (
                            <span className="text-xs">Failed</span>
                          ) : (
                            <>
                              <span className="hidden sm:inline">Mark Failed</span>
                              <span className="sm:hidden">✗</span>
                            </>
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Password Confirmation Modal for Recover Order */}
      <PasswordConfirmModal
        isOpen={showPasswordModal && pendingAction?.type === 'recover'}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={confirmRecoverOrder}
        title="Confirm Order Recovery"
        message="Please enter your password to confirm recovering this order. This will create the order and mark the payment as successful."
      />

      {/* Password Confirmation Modal for Bulk Recover */}
      <PasswordConfirmModal
        isOpen={showPasswordModal && pendingAction?.type === 'bulkRecover'}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={confirmBulkRecover}
        title="Confirm Bulk Order Recovery"
        message={`Please enter your password to confirm recovering ${pendingAction?.payments?.length || 0} order(s). This will create orders and mark payments as successful.`}
      />

      {/* Mark as Failed Modal */}
      {showMarkFailedModal && (
        <>
          <div 
            className="fixed inset-0 bg-black/60 z-[100] transition-opacity"
            onClick={() => {
              setShowMarkFailedModal(false);
              setPendingAction(null);
              setFailedReason('');
            }}
          />
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
              <button
                onClick={() => {
                  setShowMarkFailedModal(false);
                  setPendingAction(null);
                  setFailedReason('');
                }}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                disabled={markingFailed}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900">Mark Payment as Failed</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Use this if you've checked Hubtel dashboard and confirmed the payment didn't go through.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label htmlFor="reason" className="block text-sm font-medium text-gray-700 mb-1">
                    Reason (Optional)
                  </label>
                  <textarea
                    id="reason"
                    value={failedReason}
                    onChange={(e) => setFailedReason(e.target.value)}
                    disabled={markingFailed}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="e.g., Payment not found in Hubtel dashboard"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => {
                      setShowMarkFailedModal(false);
                      setPendingAction(null);
                      setFailedReason('');
                    }}
                    disabled={markingFailed}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      setShowMarkFailedModal(false);
                      setShowPasswordModal(true);
                    }}
                    disabled={markingFailed}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Confirm & Enter Password
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Password Confirmation Modal for Mark as Failed */}
      <PasswordConfirmModal
        isOpen={showPasswordModal && pendingAction?.type === 'markFailed'}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={confirmMarkAsFailed}
        title="Confirm Mark Payment as Failed"
        message="Please enter your password to confirm marking this payment as failed. This action cannot be undone."
      />

      {/* Password Confirmation Modal for Bulk Mark Failed */}
      <PasswordConfirmModal
        isOpen={showPasswordModal && pendingAction?.type === 'bulkMarkFailed'}
        onClose={() => {
          setShowPasswordModal(false);
          setPendingAction(null);
        }}
        onConfirm={confirmBulkMarkFailed}
        title="Confirm Bulk Mark as Failed"
        message={`Please enter your password to confirm marking ${pendingAction?.payments?.length || 0} payment(s) as failed. This action cannot be undone.`}
      />
    </div>
  );
}

export default UnprocessedPayments;

